import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { useUserStore, SESSION_EXPIRY_KEY, SESSION_DURATION_MS } from '@/store/userStore';

import { useThemeStore } from '@/store/themeStore';
import AppShell from '@/components/layout/AppShell';
import Login from '@/pages/Login';
import ApproveAccess from '@/pages/ApproveAccess';
import Dashboard from '@/pages/Dashboard';
import Liderazgo from '@/pages/Liderazgo';
import OPS from '@/pages/OPS';
import NearMisses from '@/pages/NearMisses';
import Mapa from '@/pages/Mapa';
import Admin from '@/pages/Admin';
import Siniestralidad from '@/pages/Siniestralidad';
import Correlacion from '@/pages/Correlacion';
import Accidentes from '@/pages/Accidentes';
import Ayuda from '@/pages/Ayuda';

const PAGE_TITLES_DOC = {
  '/': 'Valoriza | Dashboard',
  '/liderazgo': 'Valoriza | Liderazgo',
  '/ops': 'Valoriza | OPS',
  '/near-misses': 'Valoriza | Near Misses',
  '/siniestralidad': 'Valoriza | Siniestralidad',
  '/correlacion': 'Valoriza | Correlación',
  '/accidentes': 'Valoriza | Accidentes',
  '/mapa': 'Valoriza | Mapa Operacional',
  '/admin': 'Valoriza | Administración',
  '/ayuda': 'Valoriza | Manual de Usuario',
  '/login': 'Valoriza | Acceder',
};

const MODULE_FROM_PATH = {
  '/': 'dashboard', '/liderazgo': 'liderazgo', '/ops': 'ops',
  '/near-misses': 'near-misses', '/siniestralidad': 'siniestralidad',
  '/correlacion': 'correlacion', '/accidentes': 'accidentes',
  '/mapa': 'mapa', '/admin': 'admin', '/ayuda': 'ayuda',
};

function PageTitleSync() {
  const location = useLocation();
  const { isAuthenticated, token, sessionId } = useUserStore();
  useEffect(() => {
    document.title = PAGE_TITLES_DOC[location.pathname] || 'Valoriza';
    // Fire heartbeat to track module visit (fire-and-forget)
    if (isAuthenticated && token && sessionId) {
      const module = MODULE_FROM_PATH[location.pathname] || 'other';
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/heartbeat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, module }),
      }).catch(() => {});
    }
  }, [location.pathname, isAuthenticated, token, sessionId]);
  return null;
}

// ── Auto-logout after 24 h ──────────────────────────────────────────────────
function SessionExpiryGuard() {
  const { isAuthenticated, logout } = useUserStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated) return;

    const check = () => {
      const loginTime = parseInt(localStorage.getItem(SESSION_EXPIRY_KEY) || '0', 10);
      if (!loginTime) return;
      if (Date.now() - loginTime >= SESSION_DURATION_MS) {
        logout();
        toast.info('Sesión caducada. Por favor, vuelve a iniciar sesión.', { duration: 6000 });
        navigate('/login', { replace: true });
      }
    };

    check(); // check immediately on mount / route change
    const interval = setInterval(check, 60 * 1000); // re-check every minute
    return () => clearInterval(interval);
  }, [isAuthenticated, logout, navigate]);

  return null;
}

// queryClient is imported from ./lib/queryClient

function ProtectedRoute({ children, requireAdmin = false }) {
  const { isAuthenticated, user } = useUserStore();
  const location = useLocation();

  // Check both zustand state and localStorage (handles MSAL post-redirect hydration)
  let isAuthed = isAuthenticated;
  if (!isAuthed) {
    try {
      const token = localStorage.getItem('valoriza_token');
      const userData = JSON.parse(localStorage.getItem('valoriza_user') || 'null');
      if (token && userData) {
        isAuthed = true;
        // Hydrate Zustand store so TopBar and all components get user data immediately
        useUserStore.setState({ user: userData, token, isAuthenticated: true });
      }
    } catch (e) { /* ignore */ }
  }

  if (!isAuthed) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // For requireAdmin, get role from user or localStorage
  let userRole = user?.role;
  if (!userRole) {
    try {
      const userData = JSON.parse(localStorage.getItem('valoriza_user') || 'null');
      userRole = userData?.role;
    } catch (e) { /* ignore */ }
  }

  if (requireAdmin && userRole !== 'superadmin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { isAuthenticated } = useUserStore();

  return (
    <>
      <PageTitleSync />
      <SessionExpiryGuard />
      <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute><AppShell /></ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="liderazgo" element={<Liderazgo />} />
        <Route path="ops" element={<OPS />} />
        <Route path="near-misses" element={<NearMisses />} />
        <Route path="siniestralidad" element={<Siniestralidad />} />
        <Route path="correlacion" element={<Correlacion />} />
        <Route path="accidentes" element={<Accidentes />} />
        <Route path="mapa" element={<Mapa />} />
        <Route path="admin" element={<ProtectedRoute requireAdmin><Admin /></ProtectedRoute>} />
        <Route path="ayuda" element={<Ayuda />} />
      </Route>
      {/* Public routes (no login required) */}
      <Route path="approve-access/:requestId" element={<ApproveAccess />} />
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  const { theme } = useThemeStore();

  // Apply theme class to body for CSS overrides
  useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [theme]);

  return (
    <div className={`App${theme === 'dark' ? ' dark' : ''}`}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" theme={theme} richColors />
        </BrowserRouter>
      </QueryClientProvider>
    </div>
  );
}

export default App;
